import axios from 'axios';
import { z } from 'zod';
import { apiClient } from '@/lib/api-client';
import type { AnswerKeyEntry } from '@/modules/diagnostico/domain/entities/answer-key.entity';
import { toAnswerOption } from '@/modules/simulado/domain/entities/answer.entity';
import { toDifficulty } from '@/modules/simulado/domain/entities/question.entity';

/** O gabarito oficial publicado no Primum Core (`GET /exams-capture/answer-key`). */
export const ANSWER_KEY_ROUTE = '/exams-capture/answer-key';

const AnswerKeyResponseSchema = z.object({
  revision: z.number().int(),
  items: z.array(
    z.object({
      questionNumber: z.number().int().positive(),
      correctAlternativeIndex: z.number().int().min(0).nullable(),
      excluded: z.boolean(),
      difficulty: z.string().nullable().optional(),
    }),
  ),
});

/**
 * O gabarito oficial que o admin publicou no Primum Core — **a mesma fonte da
 * nota e do ranking**. `null` (404) enquanto não for publicado.
 *
 * Rota pública, sem sessão: é o que a banca divulga, sem dado de pessoa.
 */
export class AnswerKeyApiDatasource {
  async getPublished(
    exam: string,
    edition: number,
  ): Promise<AnswerKeyEntry[] | null> {
    try {
      const { data } = await apiClient.get<unknown>(ANSWER_KEY_ROUTE, {
        params: { exam, edition },
        anonymous: true,
      });
      return AnswerKeyResponseSchema.parse(data)
        .items.map(
          (item): AnswerKeyEntry => ({
            number: item.questionNumber,
            answer:
              !item.excluded && item.correctAlternativeIndex !== null
                ? toAnswerOption(item.correctAlternativeIndex)
                : null,
            isExcluded: item.excluded,
            difficulty: toDifficulty(item.difficulty ?? undefined),
          }),
        )
        .sort((a, b) => a.number - b.number);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }
}
